<template>
  <SoPageWithHeader :title="draw?.name ?? `Draw ${drawId}`">
    <div class="d-flex justify-space-between align-center mb-4">
      <SoButton
        type="text"
        title="Back to draws"
        prepend-icon="arrow-left"
        class="px-0"
        data-testid="draw-detail-back-btn"
        :to="{ name: RouteNames.AdminDrawList }"
      />
      <div class="d-flex align-center ga-3">
        <OpsDrawSelectionActions
          :draw-id="drawId"
          :status="lifecycleStatus"
          @changed="onSelectionChanged"
        />
        <OpsNotifyWinnersAction
          :draw-id="drawId"
          :status="lifecycleStatus"
          :winners="winners"
          :rewards="draw?.rewards ?? []"
          @changed="onDistributionChanged"
        />
        <OpsGrantPrizesAction
          :draw-id="drawId"
          :status="lifecycleStatus"
          :winners="winners"
          @changed="onDistributionChanged"
        />
      </div>
    </div>

    <SoAlert
      v-if="query?.isError"
      type="error"
      title="Failed to load draw"
      data-testid="draw-detail-error"
    />

    <template v-else>
      <SoRow class="mb-4">
        <SoCol :default-width="3" :lg-and-down-width="6" :mobile-width="12">
          <SoWidget data-testid="draw-detail-status-card">
            <div class="text-center">
              <div class="text-subtitle-2 text-medium-emphasis">Status</div>
              <SoChip
                :type="lifecycleChipType"
                :text="lifecycleLabel"
                size="small"
                class="mt-2"
                data-testid="draw-detail-status-chip"
              />
              <div
                v-if="lifecycleSubtitle"
                class="text-caption text-medium-emphasis mt-2"
                data-testid="draw-detail-status-subtitle"
              >
                {{ lifecycleSubtitle }}
              </div>
            </div>
          </SoWidget>
        </SoCol>
        <SoCol :default-width="3" :lg-and-down-width="6" :mobile-width="12">
          <SoWidget
            full-height
            fill-container
            data-testid="draw-detail-participants-card"
          >
            <div class="text-center my-auto">
              <div class="text-subtitle-2 text-medium-emphasis">
                Participants
              </div>
              <div
                class="text-h4 mt-1"
                data-testid="draw-detail-participant-count"
              >
                {{ participantCountDisplay }}
              </div>
            </div>
          </SoWidget>
        </SoCol>
        <SoCol :default-width="3" :lg-and-down-width="6" :mobile-width="12">
          <SoWidget
            full-height
            fill-container
            data-testid="draw-detail-winner-notification-card"
          >
            <div class="text-center my-auto">
              <div class="text-subtitle-2 text-medium-emphasis">
                Notify winners date
              </div>
              <div
                class="mt-1"
                data-testid="draw-detail-winner-notification-date"
              >
                {{ winnerNotificationDateDisplay }}
              </div>
            </div>
          </SoWidget>
        </SoCol>
      </SoRow>

      <!-- Editable details (upcoming draws only) -->
      <SoWidget
        v-if="isEditable"
        title="Details"
        class="mb-4"
        data-testid="draw-detail-schedule-edit"
      >
        <SoTextField
          v-model="scheduleForm.name"
          label="Name"
          :rules="[rules.required]"
          :disabled="updateDrawMutation?.isPending"
          class="mb-3"
          data-testid="draw-detail-edit-name"
        />
        <SoRow>
          <SoCol
            v-for="field in SCHEDULE_FIELDS"
            :key="field.key"
            :default-width="6"
            :mobile-width="12"
          >
            <SoDatePicker
              v-model="scheduleForm[field.key]"
              type="datetime"
              :label="field.label"
              :rules="[rules.required]"
              :error="
                field.key === 'winnerNotificationDateTime'
                  ? notifyDateError
                  : undefined
              "
              :disabled="updateDrawMutation?.isPending"
              :data-testid="`draw-detail-edit-${field.key}`"
            />
            <p
              v-if="field.key === 'winnerNotificationDateTime'"
              class="text-caption text-medium-emphasis mt-1"
              data-testid="draw-detail-edit-winnerNotificationDateTime-hint"
            >
              {{ WINNER_NOTIFICATION_HINT }}
            </p>
          </SoCol>
        </SoRow>
        <div class="d-flex justify-end mt-4">
          <SoButton
            title="Save"
            type="primary"
            :loading="updateDrawMutation?.isPending"
            :disabled="!canSubmit || updateDrawMutation?.isPending"
            data-testid="draw-detail-save-schedule"
            @click="saveSchedule"
          />
        </div>
      </SoWidget>

      <!-- Read-only details (active / closed draws) -->
      <SoRow v-else class="mb-4">
        <SoCol :default-width="3" :lg-and-down-width="6" :mobile-width="12">
          <SoWidget data-testid="draw-detail-start-card">
            <div class="text-center">
              <div class="text-subtitle-2 text-medium-emphasis">Start date</div>
              <div class="mt-1" data-testid="draw-detail-start-date">
                {{ startDateDisplay }}
              </div>
            </div>
          </SoWidget>
        </SoCol>
        <SoCol :default-width="3" :lg-and-down-width="6" :mobile-width="12">
          <SoWidget data-testid="draw-detail-deadline-card">
            <div class="text-center">
              <div class="text-subtitle-2 text-medium-emphasis">
                Entry deadline
              </div>
              <div class="mt-1" data-testid="draw-detail-entry-deadline">
                {{ entryDeadlineDisplay }}
              </div>
            </div>
          </SoWidget>
        </SoCol>
        <SoCol :default-width="3" :lg-and-down-width="6" :mobile-width="12">
          <SoWidget data-testid="draw-detail-completion-card">
            <div class="text-center">
              <div class="text-subtitle-2 text-medium-emphasis">
                Completion date
              </div>
              <div class="mt-1" data-testid="draw-detail-completion-date">
                {{ completionDateDisplay }}
              </div>
            </div>
          </SoWidget>
        </SoCol>
        <SoCol :default-width="3" :lg-and-down-width="6" :mobile-width="12">
          <SoWidget data-testid="draw-detail-winner-publication-card">
            <div class="text-center">
              <div class="text-subtitle-2 text-medium-emphasis">
                Winner publication
              </div>
              <div
                class="mt-1"
                data-testid="draw-detail-winner-publication-date"
              >
                {{ winnerPublicationDateDisplay }}
              </div>
            </div>
          </SoWidget>
        </SoCol>
      </SoRow>

      <OpsDrawDetailPrizes
        class="mb-4"
        :rewards="draw?.rewards ?? []"
        :draw-id="drawId"
        :editable="canEditRewards"
        :loading="query?.isFetching ?? false"
        data-testid="draw-detail-prizes"
        @saved="query?.refetch()"
      />

      <OpsDrawRerunAction
        :draw-id="drawId"
        :status="lifecycleStatus"
        :winners="winners"
        :rewards="draw?.rewards ?? []"
        @changed="onWinnersReprocessed"
      />

      <OpsDrawDetailWinners
        :winners="winners"
        :direct-debit-statuses="directDebitQuery?.data ?? {}"
        :rewards="draw?.rewards ?? []"
        :draw-id="drawId"
        :calculating="isCalculating"
        :loading="winnersQuery?.isFetching ?? false"
        :error="winnersQuery?.isError ?? false"
        @saved="winnersQuery?.refetch()"
      />
    </template>
  </SoPageWithHeader>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs from "dayjs"
  import SoPageWithHeader from "@/components/templates/SoPageWithHeader.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import rules from "@/validators/rules"
  import OpsDrawDetailPrizes from "./components/OpsDrawDetailPrizes.vue"
  import OpsDrawDetailWinners from "./components/OpsDrawDetailWinners.vue"
  import OpsDrawSelectionActions from "./components/OpsDrawSelectionActions.vue"
  import OpsDrawRerunAction from "./components/OpsDrawRerunAction.vue"
  import OpsNotifyWinnersAction from "./components/OpsNotifyWinnersAction.vue"
  import OpsGrantPrizesAction from "./components/OpsGrantPrizesAction.vue"
  import { DRAW_LIFECYCLE } from "./components/drawLifecycleConfig"
  import { LoyaltyService } from "@/services/operations/LoyaltyService"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import { useSnackbarStore } from "@/stores/snackbar"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { RouteNames } from "@/types/router"
  import { DrawStatus } from "@/models/operations/draw/draw.types"
  import { PrizeDrawStatus } from "@/api/backofficeRest/generated/model"
  import { IDrawDetail } from "@/models/operations/draw/drawDetail.types"
  import {
    arePrizesGranted,
    IDrawWinner,
  } from "@/models/operations/draw/drawWinner.types"
  import { QueryResult } from "@/api/utils/types"
  import { formatDateSlashed } from "@/helpers/dateHelper"
  import { SoChipType } from "@/types/components/SoChip"
  import {
    WINNER_NOTIFICATION_HINT,
    winnerNotificationDateError,
  } from "@/helpers/drawScheduleHelper"

  const STATUS_LABELS: Record<DrawStatus, string> = {
    upcoming: "Upcoming",
    active: "Active",
    closed: "Closed",
  }

  type ScheduleFormKey =
    | "startDateTime"
    | "entryDeadlineDateTime"
    | "completionDateTime"
    | "winnerPublicationDateTime"
    | "winnerNotificationDateTime"

  type ScheduleForm = Record<ScheduleFormKey, Date | null> & { name: string }

  const SCHEDULE_FIELDS: { key: ScheduleFormKey; label: string }[] = [
    { key: "startDateTime", label: "Start" },
    { key: "entryDeadlineDateTime", label: "Entry deadline" },
    { key: "completionDateTime", label: "Completion" },
    { key: "winnerPublicationDateTime", label: "Winner publication" },
    { key: "winnerNotificationDateTime", label: "Notify winners date" },
  ]

  const emptyScheduleForm = (): ScheduleForm => ({
    name: "",
    startDateTime: null,
    entryDeadlineDateTime: null,
    completionDateTime: null,
    winnerPublicationDateTime: null,
    winnerNotificationDateTime: null,
  })

  export default defineComponent({
    components: {
      SoPageWithHeader,
      SoAlert,
      SoButton,
      SoChip,
      SoCol,
      SoDatePicker,
      SoRow,
      SoTextField,
      SoWidget,
      OpsDrawDetailPrizes,
      OpsDrawDetailWinners,
      OpsDrawSelectionActions,
      OpsDrawRerunAction,
      OpsNotifyWinnersAction,
      OpsGrantPrizesAction,
    },
    data() {
      return {
        RouteNames,
        SCHEDULE_FIELDS,
        WINNER_NOTIFICATION_HINT,
        query: null as QueryResult<IDrawDetail | null> | null,
        updateDrawMutation: LoyaltyMutator.updateDraw(),
        winnersQuery: null as QueryResult<IDrawWinner[]> | null,
        directDebitQuery: null as QueryResult<Record<string, boolean>> | null,
        statusQuery: null as QueryResult<PrizeDrawStatus | null> | null,
        scheduleForm: emptyScheduleForm(),
        snackbar: useSnackbarStore(),
        rules,
      }
    },
    computed: {
      drawId(): string {
        return this.$route.params.drawId as string
      },
      draw(): IDrawDetail | null {
        return this.query?.data ?? null
      },
      winners(): IDrawWinner[] {
        return this.winnersQuery?.data ?? []
      },
      lifecycleStatus(): PrizeDrawStatus | null {
        return this.statusQuery?.data ?? null
      },
      // Prefer the fine lifecycle label; fall back to the coarse date-derived
      // status while the lifecycle read is still loading (null).
      lifecycleLabel(): string {
        const status = this.lifecycleStatus
        return status
          ? DRAW_LIFECYCLE[status].label
          : STATUS_LABELS[this.status]
      },
      lifecycleChipType(): SoChipType {
        const status = this.lifecycleStatus
        return status ? DRAW_LIFECYCLE[status].type : SoChipType.DEFAULT
      },
      lifecycleSubtitle(): string {
        const status = this.lifecycleStatus
        if (!status) return ""
        // The status stays WINNERS_NOTIFIED after granting (the getter is date-
        // driven and has no granted state), so reflect the grant in the subtitle.
        if (
          status === PrizeDrawStatus.WINNERS_NOTIFIED &&
          this.alreadyGranted
        ) {
          return "Prizes allocated"
        }
        return DRAW_LIFECYCLE[status].subtitle
      },
      alreadyGranted(): boolean {
        return arePrizesGranted(this.winners)
      },
      isCalculating(): boolean {
        return this.lifecycleStatus === PrizeDrawStatus.CALCULATING_WINNERS
      },
      status(): DrawStatus {
        return this.draw?.getStatus(dayjs()) ?? "upcoming"
      },
      isEditable(): boolean {
        return this.status === "upcoming"
      },
      canEditRewards(): boolean {
        return !!this.draw && this.status === "upcoming"
      },
      startDateDisplay(): string {
        return this.draw ? formatDateSlashed(this.draw.startDateTime) : "—"
      },
      entryDeadlineDisplay(): string {
        return this.draw
          ? formatDateSlashed(this.draw.entryDeadlineDateTime)
          : "—"
      },
      completionDateDisplay(): string {
        return this.draw ? formatDateSlashed(this.draw.completionDateTime) : "—"
      },
      winnerPublicationDateDisplay(): string {
        return this.draw
          ? formatDateSlashed(this.draw.winnerPublicationDateTime)
          : "—"
      },
      winnerNotificationDateDisplay(): string {
        return this.draw
          ? formatDateSlashed(this.draw.winnerNotificationDateTime)
          : "—"
      },
      participantCountDisplay(): string {
        if (!this.draw) return "—"
        return this.draw.participantCount.toLocaleString()
      },
      canSubmit(): boolean {
        return (
          this.scheduleForm.name.trim().length > 0 &&
          SCHEDULE_FIELDS.every(
            ({ key }) => this.scheduleForm[key] instanceof Date,
          ) &&
          !this.notifyDateError
        )
      },
      notifyDateError(): string | undefined {
        return winnerNotificationDateError(
          this.scheduleForm.winnerNotificationDateTime,
          this.scheduleForm.entryDeadlineDateTime,
          this.scheduleForm.winnerPublicationDateTime,
        )
      },
    },
    watch: {
      drawId: {
        immediate: true,
        handler(id: string) {
          this.query = LoyaltyService.getDrawDetail(id)
          this.winnersQuery = LoyaltyService.getDrawWinnersWithPreviousWins(id)
          this.statusQuery = LoyaltyService.getDrawStatus(id)
        },
      },
      // Loaded separately so the table renders without waiting on the per-account
      // fan-out; an unchanged set of accounts reuses the cached query.
      winners: {
        immediate: true,
        handler(winners: IDrawWinner[]) {
          this.directDebitQuery = LoyaltyService.getDirectDebitStatuses(
            winners.map((winner) => winner.billingAccountId),
          )
        },
      },
      lifecycleStatus(
        newStatus: PrizeDrawStatus | null,
        oldStatus: PrizeDrawStatus | null,
      ) {
        if (
          oldStatus === PrizeDrawStatus.CALCULATING_WINNERS &&
          newStatus !== PrizeDrawStatus.CALCULATING_WINNERS
        ) {
          this.winnersQuery?.refetch()
        }
      },
      draw: {
        immediate: true,
        handler(draw: IDrawDetail | null) {
          if (draw) {
            this.scheduleForm = {
              name: draw.name,
              startDateTime: draw.startDateTime.toDate(),
              entryDeadlineDateTime: draw.entryDeadlineDateTime.toDate(),
              completionDateTime: draw.completionDateTime.toDate(),
              winnerPublicationDateTime:
                draw.winnerPublicationDateTime.toDate(),
              winnerNotificationDateTime:
                draw.winnerNotificationDateTime.toDate(),
            }
          }
        },
      },
    },
    methods: {
      onSelectionChanged() {
        this.statusQuery?.refetch()
      },
      onWinnersReprocessed() {
        this.winnersQuery?.refetch()
      },
      onDistributionChanged() {
        // Notify flips the lifecycle status (→ WINNERS_NOTIFIED); grant flips the
        // winners to REWARDED. Refetch both so the action buttons re-evaluate.
        this.statusQuery?.refetch()
        this.winnersQuery?.refetch()
      },
      async saveSchedule() {
        if (this.updateDrawMutation.isPending || this.notifyDateError) return

        const {
          name,
          startDateTime,
          entryDeadlineDateTime,
          completionDateTime,
          winnerPublicationDateTime,
          winnerNotificationDateTime,
        } = this.scheduleForm
        if (
          !startDateTime ||
          !entryDeadlineDateTime ||
          !completionDateTime ||
          !winnerPublicationDateTime ||
          !winnerNotificationDateTime
        )
          return

        try {
          await this.updateDrawMutation.mutateAsync({
            drawId: this.drawId,
            name: name.trim(),
            startDateTime: dayjs(startDateTime).toISOString(),
            entryDeadlineDateTime: dayjs(entryDeadlineDateTime).toISOString(),
            completionDateTime: dayjs(completionDateTime).toISOString(),
            winnerPublicationDateTime: dayjs(
              winnerPublicationDateTime,
            ).toISOString(),
            winnerNotificationDateTime: dayjs(
              winnerNotificationDateTime,
            ).toISOString(),
          })
        } catch (error) {
          errorHandler.showCaughtError(error, "Failed to update schedule")
          return
        }

        this.snackbar.addSuccess("Schedule updated")
        this.query?.refetch()
      },
    },
  })
</script>
